import { createWorkspaceItemId, normalizeWorkspaceItemId } from '@elasticnote/workspace-domain';
import { projectDirectoryPath, projectFilePath } from './project-path';
import type { ProjectStorageProvider } from './types';
import { normalizeProjectItemProperties, type ProjectItemProperties } from './project-item-properties';

export const PROJECT_ITEM_METADATA_PATH = '.elasticnote/item-metadata.json';
export const PROJECT_ITEM_METADATA_CHANGED_EVENT = 'elasticnote:item-metadata-changed';
const PROJECT_ITEM_METADATA_DIR = '.elasticnote';
const MAX_TAGS_PER_ITEM = 16;
const MAX_TAG_LENGTH = 48;
const MAX_ITEMS = 10000;
const MAX_METADATA_BYTES = 2 * 1024 * 1024;
const MAX_SCANNED_ITEMS = 50000;

export type ProjectItemKind = 'file' | 'folder';
export type ProjectItemMetadataEntry = {
  /** Stable portable identity used by deep links/MCP; preserved across rename/move. */
  itemId: string;
  kind: ProjectItemKind;
  /** True only for a path tombstone; active neutral items remain addressable by itemId. */
  deleted: boolean;
  favorite: boolean;
  tags: string[];
  properties: ProjectItemProperties;
  /** First known portable creation time. Older metadata can omit it. */
  createdAt?: number;
  updatedAt: number;
};
export type ProjectItemMetadataDocument = {
  schemaVersion: 3;
  kind: 'elasticnote-item-metadata';
  items: Record<string, ProjectItemMetadataEntry>;
};

export const EMPTY_PROJECT_ITEM_METADATA: ProjectItemMetadataDocument = {
  schemaVersion: 3,
  kind: 'elasticnote-item-metadata',
  items: {},
};

export function normalizeProjectItemMetadataPath(input: string): string {
  return input.replaceAll('\\', '/').replace(/^\/+|\/+$/gu, '').split('/').filter(Boolean).join('/');
}

export function normalizeProjectItemTags(input: readonly string[]): string[] {
  const unique = new Map<string, string>();
  for (const raw of input) {
    const value = raw.trim().replace(/\s+/gu, ' ').slice(0, MAX_TAG_LENGTH);
    if (!value) continue;
    unique.set(value.toLocaleLowerCase(), value);
    if (unique.size >= MAX_TAGS_PER_ITEM) break;
  }
  return [...unique.values()];
}

export function parseProjectItemMetadata(value: unknown): ProjectItemMetadataDocument {
  if (!value || typeof value !== 'object') return structuredClone(EMPTY_PROJECT_ITEM_METADATA);
  const record = value as Record<string, unknown>;
  if (![1, 2, 3].includes(Number(record.schemaVersion)) || record.kind !== 'elasticnote-item-metadata' || !record.items || typeof record.items !== 'object' || Array.isArray(record.items)) {
    return structuredClone(EMPTY_PROJECT_ITEM_METADATA);
  }
  const candidates: Array<[string, ProjectItemMetadataEntry]> = [];
  for (const [rawPath, rawEntry] of Object.entries(record.items as Record<string, unknown>).slice(0, MAX_SCANNED_ITEMS)) {
    const path = normalizeProjectItemMetadataPath(rawPath);
    if (!path || !rawEntry || typeof rawEntry !== 'object' || Array.isArray(rawEntry)) continue;
    const entry = rawEntry as Record<string, unknown>;
    const kind: ProjectItemKind = entry.kind === 'folder' ? 'folder' : 'file';
    const tags = normalizeProjectItemTags(Array.isArray(entry.tags) ? entry.tags.filter((tag): tag is string => typeof tag === 'string') : []);
    const favorite = entry.favorite === true;
    const canonicalItemId = normalizeWorkspaceItemId(entry.itemId);
    const rawItemId = typeof entry.itemId === 'string' ? entry.itemId.trim() : '';
    const properties = normalizeProjectItemProperties(entry.properties);
    // Keep legacy asset-directory identity as a recovery alias. The website
    // and extension now agree on the 128-bit canonical itemId, but older
    // Markdown assets may still live under the historic double-prefix path.
    if (canonicalItemId && rawItemId && rawItemId.toLowerCase() !== canonicalItemId &&
        !properties['Elasticnote recovered asset item id']) {
      properties['Elasticnote recovered asset item id'] = { type: 'text', value: rawItemId };
    }
    candidates.push([path, {
      itemId: canonicalItemId,
      kind,
      deleted: entry.deleted === true || (Number(record.schemaVersion) === 1 && !favorite && tags.length === 0),
      favorite,
      tags,
      properties,
      createdAt: Number.isFinite(Number(entry.createdAt)) ? Math.max(0, Number(entry.createdAt)) : undefined,
      updatedAt: Number.isFinite(Number(entry.updatedAt)) ? Math.max(0, Number(entry.updatedAt)) : 0,
    }]);
  }
  // Active metadata is more valuable than old propagation tombstones. Keep all
  // active rows first (newest first when the catalogue itself is oversized),
  // then use remaining capacity for the newest tombstones. This prevents years
  // of moves/deletes from crowding current tags/favorites out of the bounded doc.
  const active = candidates.filter(([, entry]) => !entry.deleted).sort((left, right) => right[1].updatedAt - left[1].updatedAt);
  const tombstones = candidates.filter(([, entry]) => entry.deleted).sort((left, right) => right[1].updatedAt - left[1].updatedAt);
  const items: Record<string, ProjectItemMetadataEntry> = {};
  for (const [path, entry] of [...active, ...tombstones].slice(0, MAX_ITEMS)) items[path] = entry;
  return { schemaVersion: 3, kind: 'elasticnote-item-metadata', items };
}

export async function readProjectItemMetadata(provider: ProjectStorageProvider): Promise<ProjectItemMetadataDocument> {
  try {
    const bytes = await provider.readFile(projectFilePath(PROJECT_ITEM_METADATA_PATH));
    if (!bytes || bytes.byteLength > MAX_METADATA_BYTES) return structuredClone(EMPTY_PROJECT_ITEM_METADATA);
    return parseProjectItemMetadata(JSON.parse(new TextDecoder().decode(bytes)));
  } catch {
    return structuredClone(EMPTY_PROJECT_ITEM_METADATA);
  }
}

export async function writeProjectItemMetadata(provider: ProjectStorageProvider, document: ProjectItemMetadataDocument): Promise<void> {
  await provider.createDirectory(projectDirectoryPath(PROJECT_ITEM_METADATA_DIR)).catch(() => undefined);
  const serialized = `${JSON.stringify(parseProjectItemMetadata(document), null, 2)}\n`;
  await provider.writeFile(projectFilePath(PROJECT_ITEM_METADATA_PATH), new TextEncoder().encode(serialized));
}

async function mutateProjectItemMetadata(
  provider: ProjectStorageProvider,
  pathInput: string,
  itemKind: ProjectItemKind,
  mutate: (entry: ProjectItemMetadataEntry) => ProjectItemMetadataEntry,
): Promise<ProjectItemMetadataEntry> {
  const path = normalizeProjectItemMetadataPath(pathInput);
  if (!path) throw new Error('A file or folder path is required.');
  const document = await readProjectItemMetadata(provider);
  const current = document.items[path] ?? { itemId: createWorkspaceItemId(), kind: itemKind, deleted: false, favorite: false, tags: [], properties: {}, createdAt: Date.now(), updatedAt: 0 };
  const now = Date.now();
  const next = { ...mutate(current), deleted: false, itemId: normalizeWorkspaceItemId(current.itemId) || createWorkspaceItemId(), kind: itemKind, createdAt: current.createdAt || now, updatedAt: now };
  // Keep an empty entry as a bounded metadata tombstone. That lets “unfavorite”
  // and “remove last tag” propagate safely through portable notebook metadata
  // instead of allowing an older device to resurrect a deleted label/star.
  document.items[path] = next;
  await writeProjectItemMetadata(provider, document);
  return next;
}

export async function toggleProjectItemFavorite(provider: ProjectStorageProvider, path: string, kind: ProjectItemKind): Promise<ProjectItemMetadataEntry> {
  return mutateProjectItemMetadata(provider, path, kind, (entry) => ({ ...entry, favorite: !entry.favorite }));
}

export async function setProjectItemTags(provider: ProjectStorageProvider, path: string, kind: ProjectItemKind, tags: readonly string[]): Promise<ProjectItemMetadataEntry> {
  const normalizedTags = normalizeProjectItemTags(tags);
  return mutateProjectItemMetadata(provider, path, kind, (entry) => ({ ...entry, tags: normalizedTags }));
}

export async function setProjectItemProperties(provider: ProjectStorageProvider, path: string, kind: ProjectItemKind, properties: ProjectItemProperties): Promise<ProjectItemMetadataEntry> {
  const normalized = normalizeProjectItemProperties(properties);
  return mutateProjectItemMetadata(provider, path, kind, (entry) => ({ ...entry, properties: normalized }));
}

export function mergeProjectItemMetadata(
  localValue: unknown,
  portableValue: unknown,
): ProjectItemMetadataDocument {
  const local = parseProjectItemMetadata(localValue);
  const portable = parseProjectItemMetadata(portableValue);
  const items: Record<string, ProjectItemMetadataEntry> = {};
  const paths = new Set([...Object.keys(portable.items), ...Object.keys(local.items)]);
  for (const path of paths) {
    const localEntry = local.items[path];
    const portableEntry = portable.items[path];
    if (!localEntry) items[path] = portableEntry;
    else if (!portableEntry) items[path] = localEntry;
    else {
      const winner = localEntry.updatedAt >= portableEntry.updatedAt ? localEntry : portableEntry;
      const stableId = normalizeWorkspaceItemId(winner.itemId) || normalizeWorkspaceItemId(localEntry.itemId) || normalizeWorkspaceItemId(portableEntry.itemId);
      items[path] = { ...winner, itemId: stableId };
    }
  }
  return parseProjectItemMetadata({ schemaVersion: 3, kind: 'elasticnote-item-metadata', items });
}

export async function ensureProjectItemIdentities(
  provider: ProjectStorageProvider,
  entries?: readonly { path: string; kind: ProjectItemKind }[],
): Promise<ProjectItemMetadataDocument> {
  const document = await readProjectItemMetadata(provider);
  const sourceEntries = entries ?? (await provider.listEntries()).map((entry) => ({
    path: String(entry.path),
    kind: entry.kind === 'directory' ? 'folder' as const : 'file' as const,
  }));
  let changed = false;
  const now = Date.now();
  for (const candidate of sourceEntries.slice(0, MAX_ITEMS)) {
    const path = normalizeProjectItemMetadataPath(candidate.path);
    if (!path || path === PROJECT_ITEM_METADATA_DIR || path.startsWith(`${PROJECT_ITEM_METADATA_DIR}/`)) continue;
    const current = document.items[path];
    if (!current) {
      document.items[path] = { itemId: createWorkspaceItemId(), kind: candidate.kind, deleted: false, favorite: false, tags: [], properties: {}, createdAt: now, updatedAt: now };
      changed = true;
      continue;
    }
    const itemId = normalizeWorkspaceItemId(current.itemId);
    if (!itemId || current.kind !== candidate.kind) {
      document.items[path] = { ...current, itemId: itemId || createWorkspaceItemId(), kind: candidate.kind, deleted: false, updatedAt: Math.max(current.updatedAt, now) };
      changed = true;
    }
  }
  if (changed) await writeProjectItemMetadata(provider, document);
  return document;
}

export async function setProjectItemIdentity(
  provider: ProjectStorageProvider,
  pathInput: string,
  kind: ProjectItemKind,
  itemIdInput: string,
): Promise<ProjectItemMetadataEntry> {
  const path = normalizeProjectItemMetadataPath(pathInput);
  const itemId = normalizeWorkspaceItemId(itemIdInput);
  if (!path || !itemId) throw new Error('invalid_project_item_identity');
  const document = await readProjectItemMetadata(provider);
  for (const [candidatePath, candidate] of Object.entries(document.items)) {
    if (candidatePath !== path && !candidate.deleted && normalizeWorkspaceItemId(candidate.itemId) === itemId) {
      throw new Error('project_item_identity_conflict');
    }
  }
  const current = document.items[path];
  const next: ProjectItemMetadataEntry = {
    itemId,
    kind,
    deleted: false,
    favorite: current?.favorite === true,
    tags: current?.tags ?? [],
    properties: current?.properties ?? {},
    createdAt: current?.createdAt || Date.now(),
    updatedAt: Math.max(Date.now(), Number(current?.updatedAt) || 0),
  };
  document.items[path] = next;
  await writeProjectItemMetadata(provider, document);
  return next;
}

export async function resolveProjectItemById(
  provider: ProjectStorageProvider,
  itemIdInput: string,
): Promise<{ path: string; entry: ProjectItemMetadataEntry } | undefined> {
  const itemId = normalizeWorkspaceItemId(itemIdInput);
  if (!itemId) return undefined;
  const document = await ensureProjectItemIdentities(provider);
  for (const [path, entry] of Object.entries(document.items)) {
    if (!entry.deleted && normalizeWorkspaceItemId(entry.itemId) === itemId) return { path, entry };
  }
  return undefined;
}

export async function relocateProjectItemMetadata(
  provider: ProjectStorageProvider,
  fromInput: string,
  toInput: string,
  options: { recursive?: boolean } = {},
): Promise<void> {
  const from = normalizeProjectItemMetadataPath(fromInput);
  const to = normalizeProjectItemMetadataPath(toInput);
  if (!from || !to || from === to) return;
  const document = await readProjectItemMetadata(provider);
  const matched = Object.entries(document.items).filter(([path]) => path === from || (options.recursive === true && path.startsWith(`${from}/`)));
  if (!matched.length) return;
  const now = Date.now();
  for (const [oldPath, entry] of matched) {
    const suffix = oldPath === from ? '' : oldPath.slice(from.length);
    const newPath = `${to}${suffix}`;
    const itemId = normalizeWorkspaceItemId(entry.itemId) || createWorkspaceItemId();
    document.items[oldPath] = { itemId, kind: entry.kind, deleted: true, favorite: false, tags: [], properties: {}, updatedAt: now };
    document.items[newPath] = { ...entry, itemId, deleted: false, updatedAt: now };
  }
  await writeProjectItemMetadata(provider, document);
}

export async function removeProjectItemMetadata(
  provider: ProjectStorageProvider,
  pathInput: string,
  options: { recursive?: boolean; kind?: ProjectItemKind } = {},
): Promise<void> {
  const path = normalizeProjectItemMetadataPath(pathInput);
  if (!path) return;
  const document = await readProjectItemMetadata(provider);
  const matched = Object.entries(document.items).filter(([candidate]) => candidate === path || (options.recursive === true && candidate.startsWith(`${path}/`)));
  const now = Date.now();
  if (!matched.length) {
    // Still emit a tombstone for the requested path. A device can delete an
    // item while offline before learning about metadata added elsewhere; this
    // path-level tombstone prevents that older remote favorite/tag from winning
    // the next merge simply because the local catalogue had no row yet.
    document.items[path] = { itemId: createWorkspaceItemId(), kind: options.kind ?? 'file', deleted: true, favorite: false, tags: [], properties: {}, updatedAt: now };
  } else {
    for (const [candidate, entry] of matched) {
      document.items[candidate] = { itemId: normalizeWorkspaceItemId(entry.itemId) || createWorkspaceItemId(), kind: entry.kind, deleted: true, favorite: false, tags: [], properties: {}, updatedAt: now };
    }
  }
  await writeProjectItemMetadata(provider, document);
}
